import { createFileRoute, Navigate } from "@tanstack/react-router";
import { Box, Separator, Spinner } from "@salestrak/ui";

import { useUserSettings } from "#app/lib/hooks/use-user-settings";
import { getDefaultRouteSuffix } from "#app/lib/default-route";
import { useWidgetsFlag } from "#app/lib/hooks/use-widgets-flag";
import { useFeatureFlags } from "#app/hooks/use-feature-flags";
import { PageHeader } from "#app/components/page-header";
import { DashboardFilterProvider } from "#app/legacy-dashboard/lib/FilterContext";
import { FilterBar } from "#app/legacy-dashboard/components/FilterBar";
import OverviewScreen from "#app/legacy-dashboard/screens/OverviewScreen";

/**
 * Workspace landing, gated on WIDGETS (the widgets flag):
 *
 * - Flag OFF → the legacy Home dashboard (KPIs, golden hour, stage breakdown)
 *   renders here directly, exactly as it did before the widgets system replaced
 *   it. Its removal is what the flag gates.
 * - Flag ON → the widget surfaces own analytics, so the root redirects to the
 *   user's chosen landing route (`preferences.defaultRoute`, default Inbox).
 *   The suffix is workspace-relative and every option is non-empty, so the
 *   root never renders content of its own in this mode.
 *
 * Flags resolve async; hold on a spinner until `ready` so flag-on users never
 * flash the legacy dashboard before the redirect.
 */
function Index() {
  const { ready } = useFeatureFlags();
  const widgetsEnabled = useWidgetsFlag();
  const { preferences, isLoading } = useUserSettings();

  if (!ready || (widgetsEnabled && isLoading && !preferences)) {
    return (
      <Box fill="flex" align="center" justify="center">
        <Spinner />
      </Box>
    );
  }

  if (!widgetsEnabled) {
    return (
      <DashboardFilterProvider>
        <Box direction="column" fill="flex">
          <PageHeader title="Home" actions={<FilterBar />} />
          <Separator />
          <OverviewScreen />
        </Box>
      </DashboardFilterProvider>
    );
  }

  const suffix = getDefaultRouteSuffix(preferences?.defaultRoute);
  // Absolute `to` with the `$workspace` param inherited from the current
  // match (same idiom as workflow/index.tsx's Navigate).
  return <Navigate to={`/$workspace${suffix}` as never} replace />;
}

export const Route = createFileRoute("/(app)/$workspace/")({
  component: Index,
});
